/**
 * Test mode — a flow played on a fake clock against made-up profiles. Pure.
 *
 * The same `startRun`, `step` and `markSent` the scheduler uses, so what test
 * mode shows is what would happen: a simulator with its own copy of the rules
 * would be a second opinion, not a test. Nothing here reads or writes a real
 * contact; the browser runs it.
 *
 * The profiles are worked out from the flow's own condition, so every flow gets
 * one that goes all the way, one the condition stops, one that checks the
 * fallbacks (no first name, no time zone) and, for anything that is not
 * transactional, one that has turned these emails off.
 */
import {
  DEFAULT_TIMEZONE,
  markSent,
  startRun,
  step,
  type Contact,
  type EventIn,
  type Run,
} from './engine.js'
import { EVENT_LABELS, type DomainEvent, type Flow, type Plan, type Rule } from './flows.js'

const HOUR = 3600_000
const DAY = 24 * HOUR

/** Tue 3 Nov 2026, 07:12 in Oslo — the design's clock. */
export const SIM_START = Date.UTC(2026, 10, 3, 6, 12)

export interface SimChange {
  atMs: number
  text: string
  patch: Partial<Contact>
  /** For a rule about the run, not the person — the trial's end (T03). */
  runPatch?: Partial<Run>
}

export interface SimProfile {
  id: string
  label: string
  hint: string
  contact: Contact
  /** Something the person does while the flow waits. */
  change?: SimChange
}

export interface SimMoment {
  atMs: number
  label: string
  /** The block the run is at now; null once it has left without an exit block. */
  here: string | null
  /** Every block run so far, in order. */
  visited: string[]
  /** What each visited block did, for the note on its card. */
  notes: Record<string, string>
  log: { atMs: number; text: string }[]
  done: boolean
  exit: string | null
  /** Set from the moment the email was sent. */
  sentAtMs: number | null
}

export interface Simulation {
  moments: SimMoment[]
  /** The contact as it was when the email went, for the merge preview. */
  sentTo: Contact | null
}

// ---------------------------------------------------------------------------
// Profiles
// ---------------------------------------------------------------------------

function basePlan(event: DomainEvent | null): Plan {
  if (!event) return 'free'
  if (event === 'trial.expired') return 'free'
  if (event.startsWith('trial.') || event === 'account.created') return 'trial'
  if (event.startsWith('subscription.') || event.startsWith('billing.')) return 'premium'
  return 'free'
}

function triggerEvent(flow: Flow): DomainEvent | null {
  return flow.trigger.kind === 'event' ? flow.trigger.event : null
}

function base(flow: Flow): Contact {
  const ev = triggerEvent(flow)
  return {
    uid: 'test-profile',
    email: 'test@example.com',
    firstname: 'Test',
    plan: basePlan(ev),
    autoRenew: ev === 'subscription.cancelled' ? false : basePlan(ev) === 'premium',
    billingIssue: ev === 'billing.issue',
    marketingConsent: true,
    emailPreference: 'all',
    timezone: DEFAULT_TIMEZONE,
    hemisphere: 'north',
    lastActiveAtMs: SIM_START - 2 * DAY,
    events: {},
    sends: [],
  }
}

/** A patch that makes the rule true (`holds`) or false, with words for the change. */
function patchFor(r: Rule, holds: boolean, c: Contact, atMs: number): { patch: Partial<Contact>; runPatch?: Partial<Run>; text: string } {
  switch (r.field) {
    case 'plan': {
      const want = (r.op === 'is') === holds
      if (want) {
        return {
          patch: { plan: r.value },
          text: r.value === 'premium' ? 'Starts Premium (App Store)' : `Plan becomes ${r.value}`,
        }
      }
      const other: Plan = r.value === 'premium' ? (c.plan === 'premium' ? 'trial' : c.plan) : r.value === 'free' ? 'premium' : 'free'
      return {
        patch: { plan: other },
        text: other === 'premium' ? 'Starts Premium (App Store)' : `Plan becomes ${other}`,
      }
    }
    case 'autoRenew': {
      const v = holds ? r.value : !r.value
      return { patch: { autoRenew: v }, text: v ? 'Turns auto-renew back on' : 'Turns off auto-renew' }
    }
    case 'billingIssue': {
      const v = holds ? r.value : !r.value
      return { patch: { billingIssue: v }, text: v ? 'Payment fails again' : 'Payment recovers' }
    }
    case 'marketingConsent': {
      const v = holds ? r.value : !r.value
      return { patch: { marketingConsent: v }, text: v ? 'Gives marketing consent' : 'Withdraws marketing consent' }
    }
    case 'event':
      return holds
        ? { patch: {}, text: '' }
        : { patch: { events: { ...c.events, [r.value]: atMs } }, text: EVENT_LABELS[r.value] }
    case 'sent':
      return holds
        ? { patch: {}, text: '' }
        : { patch: { sends: [...c.sends, { flow: r.value, atMs, type: 'marketing' }] }, text: `Gets ${r.value}` }
    case 'inactiveDays': {
      const recent = (r.op === 'at-most') === holds
      return recent
        ? { patch: { lastActiveAtMs: atMs }, text: 'Opens the app' }
        : { patch: { lastActiveAtMs: atMs - (r.value + 30) * DAY }, text: `Last opened the app ${r.value + 30} days ago` }
    }
    case 'trialEndsInDays':
      // The simulated trial is seven days, which holds; shortened, it does not.
      return holds
        ? { patch: {}, text: '' }
        : { patch: {}, runPatch: { endsAtMs: atMs + DAY }, text: 'The trial is shortened to end the next day' }
  }
}

function holdingAll(flow: Flow, c: Contact): Contact {
  const cond = flow.blocks.find((b) => b.kind === 'condition')
  if (!cond || cond.kind !== 'condition') return c
  let out = c
  for (const r of cond.rules) out = { ...out, ...patchFor(r, true, out, SIM_START).patch }
  return out
}

export function profilesFor(flow: Flow): SimProfile[] {
  const ok = holdingAll(flow, base(flow))
  const out: SimProfile[] = [
    {
      id: 'through',
      label: 'Goes all the way',
      hint: 'First name Test · time zone Europe/Oslo',
      contact: ok,
    },
  ]

  const cond = flow.blocks.find((b) => b.kind === 'condition')
  if (cond && cond.kind === 'condition' && cond.rules[0]) {
    // The change lands while the run waits for its first wake, so the check —
    // made right before sending — is what stops it.
    const i = flow.blocks.indexOf(cond)
    const waitsFirst = flow.blocks.slice(0, i).some((b) => b.kind === 'wait' || b.kind === 'batch')
    const first = simulateRaw(flow, ok).moments[1]?.atMs ?? SIM_START
    // No wait before the check means nothing can happen in between: the
    // person is already in that state when the flow starts.
    const at = waitsFirst ? first + 28 * 60_000 : SIM_START
    const { patch, runPatch, text } = patchFor(cond.rules[0], false, ok, at)
    out.push({
      id: 'stopped',
      label: 'Stopped by the condition',
      hint: waitsFirst ? `${text} during the wait` : `${text} before it starts`,
      contact: ok,
      change: { atMs: at, text, patch, ...(runPatch ? { runPatch } : {}) },
    })
  }

  out.push({
    id: 'fallbacks',
    label: 'No first name or time zone',
    hint: 'Checks the fallbacks',
    contact: { ...ok, firstname: '', timezone: '' },
  })

  if (flow.type !== 'transactional') {
    out.push({
      id: 'optout',
      label: 'Has turned these emails off',
      hint: 'Preference page: nothing',
      contact: { ...ok, emailPreference: 'none' },
    })
  }
  return out
}

// ---------------------------------------------------------------------------
// The run
// ---------------------------------------------------------------------------

function firstRun(flow: Flow, contact: Contact): { run: Run; label: string } | null {
  const t = flow.trigger
  if (t.kind === 'event') {
    const expiresAtMs =
      t.event.startsWith('trial.') ? SIM_START + 7 * DAY : t.event.startsWith('subscription.') ? SIM_START + 365 * DAY : SIM_START + 30 * DAY
    const ev: EventIn = { event: t.event, uid: contact.uid, atMs: SIM_START, expiresAtMs, periodKey: 'sim' }
    const run = startRun(flow, ev, SIM_START, contact.hemisphere)
    return run ? { run, label: EVENT_LABELS[t.event] } : null
  }
  // Calendar and season starts have no event: the run begins on its day.
  return {
    run: {
      id: `${flow.id}__sim`,
      flow: flow.id,
      uid: contact.uid,
      period: 'sim',
      startedAtMs: SIM_START,
      at: flow.blocks[0]!.id,
      wakeAtMs: SIM_START,
      status: 'waiting',
      exit: null,
      log: [],
    },
    label: flow.summary,
  }
}

function simulateRaw(flow: Flow, contact0: Contact, change?: SimChange): Simulation {
  const start = firstRun(flow, contact0)
  if (!start) return { moments: [], sentTo: null }

  let contact = contact0
  let run = start.run
  let changed = false
  const log: SimMoment['log'] = [{ atMs: SIM_START, text: start.label }]
  const visited: string[] = []
  const notes: Record<string, string> = {}
  let sentAtMs: number | null = null
  let sentTo: Contact | null = null
  const moments: SimMoment[] = [
    { atMs: SIM_START, label: start.label, here: 'trigger', visited: [], notes: {}, log: [...log], done: false, exit: null, sentAtMs: null },
  ]
  let lastWait: string | null = null

  for (let i = 0; i < 12 && run.status === 'waiting'; i++) {
    const now = run.wakeAtMs
    if (change && !changed && change.atMs <= now) {
      contact = { ...contact, ...change.patch }
      if (change.runPatch) run = { ...run, ...change.runPatch }
      changed = true
      log.push({ atMs: change.atMs, text: change.text })
    }

    const label =
      i === 0
        ? flow.trigger.kind === 'event' && (flow.trigger.offsetHours ?? 0) !== 0
          ? flow.summary
          : 'The flow starts'
        : lastWait === 'batch'
          ? 'Let out in a daily batch'
          : lastWait === 'window'
          ? 'Send window opens'
          : lastWait === 'cap'
            ? 'The frequency cap frees up'
            : 'The wait is over'
    if (i === 0) {
      visited.push('trigger')
      notes.trigger = `Fired ${when(now)}`
      log.push({ atMs: now, text: `Trigger: ${flow.summary}` })
    }

    let res = step(run, flow, contact, now)
    let path = res.visited
    let tally = res.tally
    run = res.run
    const send = res.actions.find((a) => a.kind === 'send')
    if (send) {
      sentAtMs = now
      sentTo = contact
      contact = { ...contact, sends: [...contact.sends, { flow: flow.id, atMs: now, type: flow.type }] }
      notes[send.blockId] = `Sent at ${clock(now)}`
      run = markSent(run, flow, now)
      if (run.status === 'waiting' && run.wakeAtMs <= now) {
        res = step(run, flow, contact, now)
        path = [...path, ...res.visited]
        tally = [...tally, ...res.tally]
        run = res.run
      }
    }

    for (const id of path) {
      if (!visited.includes(id)) visited.push(id)
      const b = flow.blocks.find((x) => x.id === id)!
      if (b.kind === 'condition') {
        const yes = tally.includes(`${id}__yes`)
        notes[id] = yes ? 'Yes' : 'No'
        log.push({ atMs: now, text: `Condition: ${b.label.replace(/^Only if /, '')}: ${yes ? 'yes' : 'no'}` })
      }
      if (b.kind === 'send' && sentAtMs === now) log.push({ atMs: now, text: `Sent: ${b.subject}` })
      if (b.kind === 'exit') {
        notes[id] = `Left at ${clock(now)}`
        log.push({ atMs: now, text: `Exit: ${b.reason}${sentAtMs === null ? '. No email sent.' : ''}` })
      }
      if (b.kind === 'batch') notes[id] = 'Waiting for a batch'
    }

    const last = path[path.length - 1]
    const lastBlock = flow.blocks.find((x) => x.id === last)
    let here: string | null = run.at
    lastWait = null
    if (run.status === 'done') {
      here = lastBlock?.kind === 'exit' ? last! : null
      if (lastBlock?.kind !== 'exit' && run.exit) log.push({ atMs: now, text: `Stopped: ${run.exit}` })
    } else if (lastBlock && (lastBlock.kind === 'wait' || lastBlock.kind === 'batch')) {
      here = lastBlock.id
      lastWait = lastBlock.kind === 'wait' && !('hours' in lastBlock) ? 'window' : 'wait'
      notes[here] =
        lastBlock.kind === 'batch'
          ? 'Waiting for a batch'
          : `Waiting until ${clock(run.wakeAtMs)}${contact.timezone || 'hours' in lastBlock ? '' : ' Oslo time'}`
      if (lastBlock.kind === 'wait') {
        log.push({
          atMs: now,
          text: `Wait: until ${when(run.wakeAtMs)}${contact.timezone || 'hours' in lastBlock ? '' : ' (no time zone on file: Europe/Oslo)'}`,
        })
      }
    } else if (lastBlock?.kind === 'send' && run.status === 'waiting') {
      lastWait = 'cap'
      notes[last!] = `Cap full; waiting until ${when(run.wakeAtMs)}`
      log.push({ atMs: now, text: 'Frequency cap full; waiting' })
    }

    moments.push({
      atMs: now,
      label,
      here,
      visited: [...visited],
      notes: { ...notes },
      log: [...log],
      done: run.status === 'done',
      exit: run.exit,
      sentAtMs,
    })
    if (run.status === 'batch') {
      // The queue lets people out most active first, up to 3,500 a day. A
      // made-up profile is let out an hour later, as an active one would be.
      const batchId = flow.blocks.find((b) => b.kind === 'batch')!.id
      const queued = { atMs: now, text: 'In the queue: let out most active first, up to 3,500 a day' }
      log.push(queued)
      moments[moments.length - 1]!.log.push(queued)
      run = { ...run, status: 'waiting', released: true, wakeAtMs: now + HOUR }
      notes[batchId] = `Let out ${clock(run.wakeAtMs)}`
      lastWait = 'batch'
    }
  }
  return { moments, sentTo }
}

export function simulate(flow: Flow, profile: SimProfile): Simulation {
  return simulateRaw(flow, profile.contact, profile.change)
}

// ---------------------------------------------------------------------------
// Clock words — always Oslo, which is what a profile without a time zone gets
// ---------------------------------------------------------------------------

const fmt = (o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('en-GB', { timeZone: DEFAULT_TIMEZONE, ...o })
const DAY_FMT = fmt({ weekday: 'short', day: 'numeric', month: 'short' })
const TIME_FMT = fmt({ hour: '2-digit', minute: '2-digit', hour12: false })

export const clock = (ms: number) => TIME_FMT.format(ms)
export const when = (ms: number) => `${DAY_FMT.format(ms).replace(',', '')}, ${clock(ms)}`

// ---------------------------------------------------------------------------
// The email a profile gets
// ---------------------------------------------------------------------------

const ddmmyyyy = (ms: number) => {
  const d = new Date(ms)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getUTCDate())}.${p(d.getUTCMonth() + 1)}.${d.getUTCFullYear()}`
}

/**
 * Merge values and reader segment for a profile, the way a real reader with
 * the same facts would get them (`mergeValuesFor`, `variantsFor`). Absent
 * values stay absent, so the writer's fallback shows.
 */
export function mergeFor(flow: Flow, c: Contact): { values: Record<string, string>; segment: 'free' | 'trial' | 'paid' | 'cancelling' } {
  const values: Record<string, string> = { email: c.email }
  if (c.firstname) {
    values.firstname = c.firstname
    values.name = c.firstname
  }
  const ev = triggerEvent(flow)
  if (c.plan === 'trial' || c.plan === 'premium') {
    const ends = SIM_START + (ev?.startsWith('trial.') ? 7 : 365) * DAY
    values.plan = c.plan === 'trial' ? 'Premium trial' : 'Premium yearly'
    values.expires = ddmmyyyy(ends)
    values.renews = c.autoRenew ? 'renews automatically' : `ends on ${ddmmyyyy(ends)}`
  }
  const segment = c.plan === 'trial' ? 'trial' : c.plan === 'premium' ? (c.autoRenew ? 'paid' : 'cancelling') : 'free'
  return { values, segment }
}
